import { Link } from "react-router-dom";
import { Phone, ChevronRight, ShoppingCart, X, User, LogOut, Bell, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useState, useEffect, lazy, Suspense } from "react";
import logo from "@/assets/lg.svg";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

// Lazy load LanguageSwitcher to prevent initial bundle bloat and early requests
const LanguageSwitcher = lazy(() => import("@/components/LanguageSwitcher"));

const Header = () => {
  const { t, getLocalizedPath } = useLanguage();
  const {
    items,
    removeFromCart,
    getTotalPrice
  } = useCart();
  const {
    user,
    profile,
    isAuthenticated,
    logout
  } = useAuth();
  const { formatPrice } = useCurrency();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    if (isAuthenticated) {
      const messages = JSON.parse(localStorage.getItem('chatMessages') || '[]');
      const userMessages = messages.filter((m: any) => m.recipientId === user?.id && !m.read);
      setUnreadCount(userMessages.length);
    }
  }, [isAuthenticated, user]);
  return <header className="w-full">
      {/* Top Bar */}
      <div className="bg-black text-white py-3 px-4 backdrop-blur-sm">
        <div className="container mx-auto">
          <div className="grid grid-cols-[auto,1fr,auto] items-center gap-2 text-sm">
            <div className="flex items-center gap-2 md:gap-4">
              <a href="tel:+908503022603" className="flex items-center gap-1.5 md:gap-2 hover:text-primary transition-all duration-300 hover:scale-105">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">+90 850 302 26 03</span>
              </a>
            </div>
            
            <div className="text-center min-w-0 px-2">
              <span className="hidden md:inline text-xs lg:text-sm font-light text-white/90">{t("Sign up to be the first to know about discounts.")}</span>
            </div>
            
                {/* Mobile Navigation */}
                <div className="md:hidden flex items-center gap-4">
                  <CurrencySwitcher />
                  <Suspense fallback={<div className="w-8 h-8" />}>
                    <LanguageSwitcher />
                  </Suspense>
                </div>

            <div className="hidden md:flex items-center gap-4">
              <Suspense fallback={<div className="w-8 h-8" />}>
                <LanguageSwitcher />
              </Suspense>
              <div className="h-6 w-px bg-border" />
              <CurrencySwitcher />
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between gap-6 py-4">
            {/* Logo */}
            <Link to={getLocalizedPath("/")} className="flex items-center shrink-0" aria-label="Campervan Rental Turkey">
              <img 
                src={logo} 
                alt="Campervan Rental Turkey" 
                width={180} 
                height={64} 
                className="h-16 w-auto" 
                loading="eager"
                decoding="async"
              />
            </Link>

            {/* Navigation Menu - Desktop */}
            <nav className="hidden lg:flex items-center gap-8">
              <Link to={getLocalizedPath("/services")} className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                {t("Our Services")}
              </Link>
              <Link to={getLocalizedPath("/about")} className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                {t("About Us")}
              </Link>
              <Link to={getLocalizedPath("/contact")} className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                {t("Contact")}
              </Link>
              <Link to={getLocalizedPath("/blog")} className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                {t("Blog")}
              </Link>
              <Link to={getLocalizedPath("/faq")} className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                {t("FAQ")}
              </Link>
              <Link to={getLocalizedPath("/camping-places-turkey")} className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                {t("Campsites in Turkey")}
              </Link>
              <Link to={getLocalizedPath("/partner-program")} className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                {t("Partner Program")}
              </Link>
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* Mobile Menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden"
                    aria-label={t("Open menu")}
                  >
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px]">
                  <SheetHeader className="mb-6">
                    <SheetTitle>{t("Menu")}</SheetTitle>
                  </SheetHeader>
                  <nav className="flex flex-col gap-1">
                    <Link to={getLocalizedPath("/services")} className="px-4 py-3 text-sm font-medium hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                      {t("Our Services")}
                    </Link>
                    <Link to={getLocalizedPath("/about")} className="px-4 py-3 text-sm font-medium hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                      {t("About Us")}
                    </Link>
                    <Link to={getLocalizedPath("/contact")} className="px-4 py-3 text-sm font-medium hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                      {t("Contact")}
                    </Link>
                    <Link to={getLocalizedPath("/blog")} className="px-4 py-3 text-sm font-medium hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                      {t("Blog")}
                    </Link>
                    <Link to={getLocalizedPath("/faq")} className="px-4 py-3 text-sm font-medium hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                      {t("FAQ")}
                    </Link>
                    <Link to={getLocalizedPath("/camping-places-turkey")} className="px-4 py-3 text-sm font-medium hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                      {t("Campsites in Turkey")}
                    </Link>
                    <Link to={getLocalizedPath("/partner-program")} className="px-4 py-3 text-sm font-medium hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                      {t("Partner Program")}
                    </Link>
                    
                    <div className="border-t mt-4 pt-4 space-y-2">
                      {/* User Profile / Login Button */}
                      {isAuthenticated ? <Link to={getLocalizedPath("/dashboard")} className="flex items-center gap-2 px-4 py-3 text-sm font-medium hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                          <User className="w-4 h-4" />
                          {t("My Dashboard")}
                        </Link> : <Link to={getLocalizedPath("/login")} className="flex items-center gap-2 px-4 py-3 text-sm font-medium hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                          <User className="w-4 h-4" />
                          {t("Login / Sign Up")}
                        </Link>}
                      
                      {/* Get a Quote Button */}
                      <Link to={getLocalizedPath("/get-quote")} className="w-full" onClick={() => setMobileMenuOpen(false)}>
                        <Button className="w-full bg-black hover:bg-black/90 text-white font-medium">
                          {t("Get a Quote")}
                        </Button>
                      </Link>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>

              {/* Notifications */}
              {isAuthenticated && <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative hidden md:flex">
                      <Bell className="w-5 h-5" />
                      {unreadCount > 0 && <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                          {unreadCount}
                        </Badge>}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 bg-popover z-[100]">
                    <DropdownMenuLabel>{t("Recent Notifications")}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="flex flex-col items-start py-3">
                      <div className="font-medium">{t("Welcome to Campervan Rental Turkey!")}</div>
                      <div className="text-sm text-muted-foreground">
                        {t("Thank you for joining us")}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">{t("Today")}</div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <Link to={getLocalizedPath("/dashboard")} className="w-full">
                        {t("View all notifications")}
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>}

              {/* User Profile / Login */}
              {isAuthenticated ? <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="hidden md:flex">
                      <User className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-popover z-[100]">
                    <DropdownMenuLabel>
                      <div className="flex flex-col">
                        <div className="font-medium">{profile?.first_name} {profile?.last_name}</div>
                        <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to={getLocalizedPath("/dashboard")} className="cursor-pointer">
                        <User className="mr-2 h-4 w-4" />
                        {t("My Dashboard")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="cursor-pointer">
                      <LogOut className="mr-2 h-4 w-4" />
                      {t("Logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu> : <Link to={getLocalizedPath("/login")} className="hidden md:block">
                  <Button variant="ghost" size="icon" aria-label={t("Sign in")}>
                    <User className="w-5 h-5" />
                  </Button>
                </Link>}

              {/* Shopping Cart */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative"
                    aria-label={t("Open cart")}
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {items.length > 0 && <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                        {items.length}
                      </Badge>}
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-full sm:max-w-lg">
                  <SheetHeader>
                    <SheetTitle>{t("Shopping Cart")} ({items.length})</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-4">
                    {items.length === 0 ? <p className="text-center text-muted-foreground py-8">{t("Your cart is empty")}</p> : <>
                        {items.map(item => <div key={item.caravan.id} className="flex gap-4 border-b pb-4">
                            <img 
                            src={item.caravan.images[0]} 
                            alt={item.caravan.title} 
                            width={80}
                            height={80}
                            className="w-20 h-20 object-cover rounded" 
                          />
                            <div className="flex-1">
                              <h4 className="font-semibold">{item.caravan.title}</h4>
                              <p className="text-sm text-muted-foreground">{item.days} days</p>
                              <p className="text-sm font-bold text-primary">{formatPrice(item.totalPrice)}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeFromCart(item.caravan.id)}
                              aria-label={t("Remove from cart")}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>)}
                        <div className="pt-4 space-y-4">
                          <div className="flex justify-between text-lg font-bold">
                            <span>{t("Total:")}</span>
                            <span>{formatPrice(getTotalPrice())}</span>
                          </div>
                          <Link to={getLocalizedPath("/cart")}>
                            <Button className="w-full">{t("Go to Cart")}</Button>
                          </Link>
                        </div>
                      </>}
                  </div>
                </SheetContent>
              </Sheet>
              
              {/* Get Quote Button */}
              <Link to={getLocalizedPath("/get-quote")} className="hidden md:inline-flex">
                <Button className="bg-black hover:bg-black/90 text-white px-6 text-sm font-medium">
                  {t("Get a Quote")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>;
};
export default Header;
